import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as EvaluateIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  fetchEvaluationSummary,
  createEvaluation,
  fetchEvaluation,
  updatePolledEvaluation,
  stopPolling,
  setSuccessMessage,
  setCurrentEvaluationError,
  setErrorMessage,
  clearMessages
} from './evaluationsSlice';
import evaluationsApi from '../../api/evaluations';
import DWLFLoader from '../../components/DWLFLoader';

// Backfill-depth choices for the events Evaluate. Values are calendar days
// passed through as `lookbackDays`; the evaluator walks candles back that far
// (larger-than-history is safe — it just starts from the earliest candle).
const LOOKBACK_OPTIONS = [
  { label: '13 months', value: 400 },
  { label: '2 years', value: 730 },
  { label: '5 years', value: 1825 },
  { label: 'Max', value: 4000 },
];
const DEFAULT_LOOKBACK_DAYS = 730;

const EvaluationStatusBanner = ({ type = 'both' }) => {
  const dispatch = useDispatch();
  // Backfill depth for the events Evaluate (default two years). Only sent for
  // type === 'events'; strategy evaluation keeps its existing behaviour.
  const [lookbackDays, setLookbackDays] = useState(DEFAULT_LOOKBACK_DAYS);
  const {
    summary,
    summaryLoading,
    currentEvaluation,
    currentEvaluationLoading,
    currentEvaluationError,
    successMessage,
    isPolling,
    pollingRequestId
  } = useSelector((state) => state.evaluations);

  // Fetch summary on mount
  useEffect(() => {
    dispatch(fetchEvaluationSummary());
  }, [dispatch]);

  // Polling effect with proper cancellation handling
  useEffect(() => {
    if (!isPolling || !pollingRequestId) return;

    let isCancelled = false;

    const poll = async () => {
      try {
        const evaluation = await evaluationsApi.getEvaluation(pollingRequestId);
        
        // Don't dispatch if polling was cancelled while request was in-flight
        if (isCancelled) return;

        dispatch(updatePolledEvaluation(evaluation));

        if (evaluation.status === 'completed' || evaluation.status === 'failed') {
          dispatch(stopPolling());
          dispatch(fetchEvaluationSummary()); // Refresh summary
          if (evaluation.status === 'completed') {
            dispatch(setSuccessMessage('Evaluation completed successfully!'));
            dispatch(setCurrentEvaluationError(null));
          } else {
            const errorMessage = evaluation.errorMessage || 'Evaluation failed. Please try again.';
            dispatch(setCurrentEvaluationError(errorMessage));
            dispatch(setErrorMessage(errorMessage));
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Polling error:', error);
        }
      }
    };

    const pollInterval = setInterval(poll, 3000);

    return () => {
      isCancelled = true;
      clearInterval(pollInterval);
    };
  }, [isPolling, pollingRequestId, dispatch]);

  useEffect(() => {
    if (!successMessage) return;

    const timeoutId = setTimeout(() => {
      dispatch(clearMessages());
    }, 4000);

    return () => clearTimeout(timeoutId);
  }, [successMessage, dispatch]);

  const handleEvaluate = useCallback(() => {
    const options = { type, mode: 'changedOnly' };
    // Backfill depth applies to event fires; leave strategy evaluation untouched.
    if (type === 'events' && lookbackDays) {
      options.lookbackDays = lookbackDays;
    }
    dispatch(createEvaluation(options));
  }, [dispatch, type, lookbackDays]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchEvaluationSummary());
  }, [dispatch]);

  // Calculate dirty items count
  const dirtyEventsCount = summary?.dirtyItems?.events || 0;
  const dirtyStrategiesCount = summary?.dirtyItems?.strategies || 0;
  const totalDirtyItems = dirtyEventsCount + dirtyStrategiesCount;
  const dirtyItemsCount = type === 'events'
    ? dirtyEventsCount
    : type === 'strategies'
      ? dirtyStrategiesCount
      : totalDirtyItems;

  // Quota info. Admins get an unlimited server-side bypass, surfaced as
  // `bypass: 'admin'` with limit/remaining = null. Treat that as unlimited —
  // otherwise `null || 0` collapses to "0 left" and disables the Evaluate
  // button for every admin account (which blocked the demo account).
  const isUnlimitedQuota = summary?.quota?.daily?.bypass === 'admin'
    || summary?.quota?.daily?.limit === null;
  const quotaUsed = summary?.quota?.daily?.used || 0;
  const quotaLimit = summary?.quota?.daily?.limit || 20;
  const quotaRemaining = isUnlimitedQuota
    ? Infinity
    : (summary?.quota?.daily?.remaining ?? 0);

  // Current evaluation progress
  const isEvaluating = currentEvaluation?.status === 'pending' || currentEvaluation?.status === 'processing';
  const processedItems = currentEvaluation?.summary?.processedItems ?? 0;
  const totalItems = currentEvaluation?.summary?.totalItems ?? 0;
  const evaluationProgress = totalItems > 0
    ? (processedItems / totalItems) * 100
    : 0;

  const shouldRenderBanner = dirtyItemsCount > 0 || isEvaluating || currentEvaluationError || successMessage;

  return (
    <AnimatePresence>
      {shouldRenderBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: isEvaluating
                ? 'var(--dwlf-accent-08)'
                : 'var(--dwlf-warn-08)',
              border: `1px solid ${isEvaluating ? 'var(--dwlf-accent-30)' : 'var(--dwlf-warn-30)'}`,
              borderRadius: 0,
              position: 'relative',
              overflow: 'hidden'
            }}
          >

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              {/* Left section: Status info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {isEvaluating ? (
                  <>
                    <DWLFLoader size="small" showText={false} />
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 500, color: 'var(--dwlf-accent)' }}>
                      Evaluating...
                    </Typography>
                    {currentEvaluation?.summary && (
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem', color: 'var(--dwlf-ink-70)' }}>
                        {processedItems} / {totalItems} items
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <PendingIcon sx={{ color: 'var(--dwlf-warn)' }} />
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 500, color: 'var(--dwlf-warn)' }}>
                      {dirtyItemsCount} item{dirtyItemsCount !== 1 ? 's' : ''} need{dirtyItemsCount === 1 ? 's' : ''} evaluation
                    </Typography>
                    {type !== 'strategies' && dirtyEventsCount > 0 && (
                      <Chip
                        label={`${dirtyEventsCount} event${dirtyEventsCount !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{ 
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: '0.7rem',
                          borderRadius: 0,
                          bgcolor: 'var(--dwlf-warn-15)', 
                          color: 'var(--dwlf-warn)',
                          border: '1px solid var(--dwlf-warn-30)'
                        }}
                      />
                    )}
                    {type !== 'events' && dirtyStrategiesCount > 0 && (
                      <Chip
                        label={`${dirtyStrategiesCount} strateg${dirtyStrategiesCount !== 1 ? 'ies' : 'y'}`}
                        size="small"
                        sx={{ 
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: '0.7rem',
                          borderRadius: 0,
                          bgcolor: 'var(--dwlf-warn-15)', 
                          color: 'var(--dwlf-warn)',
                          border: '1px solid var(--dwlf-warn-30)'
                        }}
                      />
                    )}
                  </>
                )}
              </Box>

              {/* Right section: Actions and quota */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Quota indicator */}
                <Tooltip title={isUnlimitedQuota ? `${quotaUsed} evaluations used today · admin (unlimited)` : `${quotaUsed}/${quotaLimit} evaluations used today`}>
                  <Chip
                    label={isUnlimitedQuota ? 'Unlimited' : `${quotaRemaining} left`}
                    size="small"
                    sx={{
                      fontFamily: '"IBM Plex Mono", monospace',
                      fontSize: '0.7rem',
                      borderRadius: 0,
                      bgcolor: quotaRemaining > 5 ? 'var(--dwlf-ink-10)' : 'var(--dwlf-down-20)',
                      color: quotaRemaining > 5 ? 'var(--dwlf-ink-70)' : 'var(--dwlf-down)',
                      border: `1px solid ${quotaRemaining > 5 ? 'var(--dwlf-ink-20)' : 'var(--dwlf-down-40)'}`,
                    }}
                  />
                </Tooltip>

                {/* Refresh button */}
                <Tooltip title="Refresh status">
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    disabled={summaryLoading}
                    sx={{ 
                      color: 'var(--dwlf-ink-60)',
                      borderRadius: 0,
                      '&:hover': { color: 'var(--dwlf-accent)' }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                {/* Backfill-depth selector (events only) — how far back an
                    Evaluate walks candles to populate historical fires. */}
                {type === 'events' && !isEvaluating && (
                  <Tooltip title="How far back to backfill fires when you Evaluate">
                    <Select
                      value={lookbackDays}
                      onChange={(e) => setLookbackDays(e.target.value)}
                      disabled={currentEvaluationLoading}
                      size="small"
                      sx={{
                        minWidth: 96,
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.7rem',
                        letterSpacing: '0.04em',
                        color: 'var(--dwlf-ink-85)',
                        borderRadius: 0,
                        backgroundColor: 'var(--dwlf-ink-04)',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'var(--dwlf-ink-20)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--dwlf-accent)' },
                        '.MuiSvgIcon-root': { color: 'var(--dwlf-ink-60)' },
                      }}
                      MenuProps={{ PaperProps: { sx: {
                        backgroundColor: 'var(--dwlf-bg)',
                        color: 'var(--dwlf-ink-85)',
                        fontFamily: '"Space Mono", monospace',
                        borderRadius: 0,
                      } } }}
                    >
                      {LOOKBACK_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.7rem' }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Tooltip>
                )}

                {/* Evaluate button */}
                {!isEvaluating && (
                  <Button
                    variant="contained"
                    startIcon={currentEvaluationLoading ? <DWLFLoader size="tiny" /> : <EvaluateIcon />}
                    onClick={handleEvaluate}
                    disabled={currentEvaluationLoading || quotaRemaining === 0 || dirtyItemsCount === 0}
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.7rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      borderRadius: 0,
                      backgroundColor: 'var(--dwlf-accent)',
                      color: 'var(--dwlf-bg)',
                      '&:hover': {
                        backgroundColor: 'var(--dwlf-text)'
                      },
                      '&:disabled': {
                        backgroundColor: 'var(--dwlf-ink-12)',
                        color: 'var(--dwlf-ink-40)'
                      }
                    }}
                  >
                    Evaluate
                  </Button>
                )}
              </Box>
            </Box>

            {/* Progress bar for active evaluation */}
            {isEvaluating && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={evaluationProgress}
                  sx={{
                    height: 2,
                    borderRadius: 0,
                    bgcolor: 'var(--dwlf-accent-20)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'var(--dwlf-accent)',
                      borderRadius: 0
                    }
                  }}
                />
              </Box>
            )}

            {/* Success message */}
            {successMessage && !currentEvaluationError && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">{successMessage}</Typography>
              </Box>
            )}

            {/* Error message */}
            {currentEvaluationError && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon fontSize="small" />
                <Typography variant="body2">{currentEvaluationError}</Typography>
              </Box>
            )}
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EvaluationStatusBanner;
